import React, { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, TextField, Typography } from '@mui/material';
import { invoiceService, type InvoiceCatalogProduct, type InvoiceCurrency } from '../services/invoiceService';

const priceSourceLabel = {
  variant: 'Shop Admin variant Price',
  item: 'Shop Admin product Price',
  parent: 'Shop Admin parent Price',
};

interface Props {
  open: boolean;
  currency: InvoiceCurrency;
  onClose: () => void;
  onSelect: (product: InvoiceCatalogProduct) => void;
}

export const InvoiceCatalogPicker: React.FC<Props> = ({ open, currency, onClose, onSelect }) => {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<InvoiceCatalogProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let current = true;
    setBusy(true); setError(''); setSearched(false); setQuery('');
    invoiceService.catalog('').then((result) => {
      if (!current) return;
      setItems(result.items); setTotal(result.total); setSearched(true);
    }).catch((reason) => {
      if (current) setError(reason instanceof Error ? reason.message : 'Catalog unavailable');
    }).finally(() => { if (current) setBusy(false); });
    return () => { current = false; };
  }, [open]);

  const search = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      const result = await invoiceService.catalog(query.trim());
      setItems(result.items); setTotal(result.total); setSearched(true);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Catalog unavailable');
    } finally { setBusy(false); }
  };

  return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="invoice-picker-title">
    <DialogTitle id="invoice-picker-title">Find inventory product</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Search by name or SKU. BBD prices use the public Price in Shop Admin, before VAT. Inventory Unit Price is a cost/value field and is not used for invoices.
        {currency === 'BBD'
          ? ' An item without a public Price starts at BBD 0.00 for a one-time invoice price.'
          : ' Enter the USD unit price on the invoice after selecting a product.'}
      </Typography>
      <Stack component="form" direction={{ xs: 'column', sm: 'row' }} spacing={1} onSubmit={search} mb={2}>
        <TextField label="Product name or SKU" value={query} onChange={(event) => setQuery(event.target.value)} size="small" autoFocus fullWidth />
        <Button type="submit" variant="contained" disabled={busy}>{busy ? 'Searching…' : 'Search products'}</Button>
      </Stack>
      {error && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{error}</Alert>}
      {busy && <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={18} /><span>Searching inventory…</span></Stack>}
      {!busy && !error && searched && <Typography role="status" variant="body2" mb={1}>
        {total === 0 ? 'No matching products.' : `Showing ${items.length} of ${total} matching products.`}
        {total > items.length && ' Refine the search by name or SKU to find more.'}
      </Typography>}
      {!busy && !error && <Stack spacing={1} sx={{ maxHeight: 400, overflowY: 'auto' }} aria-label="Product search results">
        {items.map((product) => {
          return <Paper key={product.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
              <ProductDescription product={product} currency={currency} />
              <Button variant="outlined" size="small"
                aria-label={`Add ${product.name} to invoice`} onClick={() => onSelect(product)}>Add to invoice</Button>
            </Stack>
          </Paper>;
        })}
      </Stack>}
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
  </Dialog>;
};

const ProductDescription: React.FC<{ product: InvoiceCatalogProduct; currency: InvoiceCurrency }> = ({ product, currency }) => <Stack>
  <Typography fontWeight={700}>{product.name}</Typography>
  <Typography variant="body2">SKU {product.sku} · {product.fulfilment_type === 'produce' ? 'Produce — made to order' : 'Buy — physical stock'}</Typography>
  <Typography variant="body2">
    {product.bbd_price === null ? 'No BBD public Price set in Shop Admin'
      : `BBD ${product.bbd_price} before VAT · ${priceSourceLabel[product.price_source || 'item']}`}
  </Typography>
  {product.fulfilment_type === 'buy' && product.on_hand != null && <Typography variant="caption" color="text.secondary">
    On hand: {product.on_hand}. Availability after reservations is checked when issuing.
  </Typography>}
  {product.bbd_price === null && <Typography variant="caption" color="text.secondary">
    {currency === 'BBD' ? 'Add at BBD 0.00, then enter a one-time invoice price and reason. Shop Admin Price will stay unchanged.' : 'You can add this item after entering its USD invoice price.'}
  </Typography>}
</Stack>;
